import { observer } from 'mobx-react';
import React from 'react';
import { BadgePicker, BadgePickerOption } from '@components/badge-picker';
import { RadioGroup } from '@components/radio-group';
import { FieldError } from '@components/textfield';
import LocalizedTextField from '@components/textfield/localized_textfield';
import { injectTSDI } from '@core/tsdi';
import { BadgeImageUpload } from '@src/badges/badge-image-upload';
import { ChallengeDataStore } from '../data-store';
import { ChallengePresentationStore } from '../presentation/store';
import { ChallengeRewardStore, RewardFormType } from './store';

const rewardTypeOptions: { label: string; value: RewardFormType }[] = [
    { label: 'None', value: 'NONE' },
    { label: 'Existing Badge', value: 'BADGE' },
    { label: 'New Badge', value: 'NEW_BADGE' }
];

export const RewardStep = observer(() => {
    const { disabledRewardEdit } = injectTSDI(ChallengeDataStore);
    const rewardStore = injectTSDI(ChallengeRewardStore);
    const presentationStore = injectTSDI(ChallengePresentationStore);

    const { fields, selectedBadgeLabel } = rewardStore;

    const handleRewardTypeChange = (type: RewardFormType) => {
        const challengeName = presentationStore.form.$.name.value;
        rewardStore.setRewardType(type, challengeName);
    };

    const handleBadgeChange = (badge: BadgePickerOption | null) => {
        if (badge) {
            const name = badge.name;
            const label = name['en'] || name[Object.keys(name)[0]] || 'Unnamed';
            rewardStore.setBadge({
                id: badge.id,
                name: badge.name,
                levelIndex: badge.levelIndex,
                imageUrl: badge.imageUrl,
                label
            });
        } else {
            rewardStore.setBadge(null);
        }
    };

    const badgePickerValue: BadgePickerOption | null =
        fields.badgeTypeId.value != null
            ? {
                  id: fields.badgeTypeId.value,
                  name: (fields.badgeName.value || {}) as Record<
                      string,
                      string
                  >,
                  categoryName: {},
                  levelIndex: fields.badgeLevelIndex.value,
                  imageUrl: fields.badgeImageUrl.value
              }
            : null;

    return (
        <div className="flex flex-col gap-4">
            <RadioGroup<RewardFormType>
                label="Reward"
                options={rewardTypeOptions}
                value={fields.rewardType.value}
                onChange={handleRewardTypeChange}
                disabled={disabledRewardEdit}
            />

            {fields.rewardType.value === 'BADGE' && (
                <div className="flex flex-col gap-1.5">
                    <BadgePicker
                        value={badgePickerValue}
                        onChange={handleBadgeChange}
                        label="Challenge Reward Badge"
                        disabled={disabledRewardEdit}
                    />
                    <FieldError error={fields.badgeTypeId.error} />
                    {selectedBadgeLabel && (
                        <p className="text-sm text-muted-foreground">
                            Selected: {selectedBadgeLabel}
                        </p>
                    )}
                </div>
            )}

            {fields.rewardType.value === 'NEW_BADGE' && (
                <>
                    <LocalizedTextField
                        type="text"
                        label="Badge Name"
                        required
                        value={fields.newBadgeName.value}
                        onChange={(value) => {
                            fields.newBadgeName.onChange(value);
                            rewardStore.validateReward(true);
                        }}
                        disabled={disabledRewardEdit}
                        error={fields.newBadgeName.error}
                    />
                    <LocalizedTextField
                        type="text"
                        label="Badge Description"
                        value={fields.newBadgeDescription.value || {}}
                        onChange={(value) =>
                            fields.newBadgeDescription.onChange(value)
                        }
                        disabled={disabledRewardEdit}
                        multiline
                        maxRows={3}
                    />
                    <div className="flex flex-col gap-1.5">
                        {fields.badgeImageUrl.value &&
                            !fields.newBadgeImage.value && (
                                <div className="mb-2">
                                    <span className="text-xs text-muted-foreground">
                                        Current badge image
                                    </span>
                                    <img
                                        src={fields.badgeImageUrl.value}
                                        className="mt-1 block size-[120px] rounded-md object-contain"
                                    />
                                </div>
                            )}
                        <BadgeImageUpload
                            label="Badge Image"
                            initialImage={fields.newBadgeImage.value}
                            onUploaded={(image) => {
                                fields.newBadgeImage.onChange(image);
                                rewardStore.validateReward(true);
                            }}
                            onRemoved={() => {
                                fields.newBadgeImage.onChange(undefined);
                                rewardStore.validateReward(true);
                            }}
                        />
                        <FieldError error={fields.newBadgeImage.error} />
                    </div>
                </>
            )}
        </div>
    );
});
